import moment from 'moment-timezone'

export interface NormalizedOrder {
  id: string
  Vehicle: string
  Driver: string
  driverUserName: string
  OrderStatus: string
  OpsStatuses: any[]
  StartWindow: string | null
  EndWindow: string | null
  Region: any
  TimeZone: any
  invoice: string
  client: string
  customer: string
  address: string
  geocodes: { lat: number; long: number }
  orderType: string
  orderTypeShort: string
  serviceType: string
  vehicleType: string
  productJson: any[]
  productCount: number
  totalQuantity: number
  quantityType: string
  totalVolume: number
  totalWeight: number
  isTruckLocked: boolean
  linkedOrder: string
  linkedRef: string
  /** The full Firestore doc data — handed to the order editor and used for writes. */
  raw: any
}

/**
 * Convert a Firestore Timestamp (or compatible) into the ISO-string format the
 * dashboard's StopCell-derived UI expects ("YYYY-MM-DDTHH:mm:00.000Z").
 */
function tsToIso(ts: any, timeZone?: string): string | null {
  if (!ts) return null
  const seconds = typeof ts.seconds === 'number' ? ts.seconds : (ts._seconds ?? null)
  if (seconds === null) {
    if (typeof ts === 'string') return ts
    return null
  }
  const m = timeZone ? moment.tz(seconds * 1000, timeZone) : moment(seconds * 1000)
  return m.format('YYYY-MM-DDTHH:mm') + ':00.000Z'
}

/**
 * Mirrors getDropoffAddressOptimizer:
 *   Apt***StDr***Unit***CityTown***PostalCode***Province***Country
 */
function buildAddress(f: any): string {
  const parts = [
    f.DropoffApt, f.DropoffStDr, f.DropoffUnit,
    f.DropoffCityTown, f.DropoffPostalCode, f.DropOffProvince, f.DropoffCountry,
  ].map((v) => String(v ?? '').trim())
  return parts.join('***')
}

function buildClient(f: any): string {
  const bu = f?.Vendor?.BusinessUnit ?? ''
  const sub = f?.Vendor?.BusinessSubUnit ?? ''
  if (!bu && !sub) return ''
  return `${bu}***${sub}`
}

/**
 * Mirror TMS_PORTAL's getRoutes linkedOrder extraction:
 *   fields.LinkedOrders.Logistics.{Pickup|Delivery} = "Name***<docId>"
 * For a Pickup order we want the linked Delivery, and vice versa.
 */
function extractLinkedOrderId(f: any): string {
  const orderType = String(f?.OrderType ?? 'Delivery').replace('clearD - ', '').toLowerCase()
  const isPickup = orderType === 'pickup'
  const opposite = isPickup ? 'Delivery' : 'Pickup'
  const raw = f?.LinkedOrders?.Logistics?.[opposite] || ''
  if (typeof raw === 'string' && raw.includes('***')) {
    return raw.split('***')[1] || ''
  }
  if (typeof raw === 'string' && raw) return raw
  return f?.linkedOrder || f?.LinkedOrder || f?.linkedOrderId || f?.LinkedOrderId || ''
}

function extractLinkedRef(f: any): string {
  const lo = f?.LinkedOrders
  return typeof lo === 'string' ? lo : ''
}

/** A Firestore QueryDocumentSnapshot or any `{ id, data() }` compatible object. */
export interface OrderDocLike {
  id: string
  data: () => any
}

export function normalizeOrder(doc: OrderDocLike): NormalizedOrder {
  const f = doc.data()?.fields ?? doc.data() ?? {}
  const opsStatuses = Array.isArray(f.OpsStatuses) ? f.OpsStatuses : []
  const orderType: string = f.OrderType ?? ''
  // Driver is stored as "Name***email" in Firestore. Card UI wants the name; the
  // USERS lookup needs the email (driverAppUserName).
  const driverRaw = typeof f.Driver === 'string' ? f.Driver : ''
  const driverParts = driverRaw.split('***')
  const driverName = driverParts[0] || ''
  const driverUserName = driverParts[1] || driverParts[0] || ''
  return {
    id: doc.id,
    Vehicle: f.Vehicle ?? '',
    Driver: driverName,
    driverUserName,
    OrderStatus: f.OrderStatus ?? '',
    OpsStatuses: opsStatuses,
    StartWindow: tsToIso(f.StartWindow, f.TimeZone),
    EndWindow: tsToIso(f.EndWindow, f.TimeZone),
    Region: f.Region,
    TimeZone: f.TimeZone,
    invoice: f.Invoice ?? doc.id,
    client: buildClient(f),
    // Match getRoutes' precedence (functions OPTIMIZER/helpers/utils.ts:
    // `CompanyName || CustomerName || ''`) so refreshStop's editor-close patch
    // doesn't clobber a CompanyName-derived customer name back to empty.
    customer: f.CompanyName || f.CustomerName || '',
    address: buildAddress(f),
    geocodes: {
      lat: typeof f.Latitude === 'number' ? f.Latitude : 0,
      long: typeof f.Longitude === 'number' ? f.Longitude : 0,
    },
    orderType,
    orderTypeShort: orderType ? orderType[0] : '',
    serviceType: f.ServiceType ?? '',
    vehicleType: f.VehicleType ?? '',
    productJson: Array.isArray(f.ProductJson) ? f.ProductJson : [],
    productCount: typeof f.ProductCount === 'number'
      ? f.ProductCount
      : (Array.isArray(f.ProductJson) ? f.ProductJson.length : 0),
    totalQuantity: Array.isArray(f.ProductJson)
      ? f.ProductJson.reduce((sum: number, p: any) => sum + (Number(p?.Quantity) || 0), 0)
      : 0,
    quantityType: f.QuantityType
      || (Array.isArray(f.ProductJson) && f.ProductJson[0]?.QuantityType)
      || '',
    // Prefer the order-level totals; fall back to summing ProductJson
    // (Volume/Weight × Quantity) — same rule the ShipmentDetails tab uses.
    totalVolume: Number(f.TotalVolume) > 0
      ? Number(f.TotalVolume)
      : (Array.isArray(f.ProductJson)
        ? f.ProductJson.reduce((sum: number, p: any) => sum + (Number(p?.Volume) || 0) * (Number(p?.Quantity) || 1), 0)
        : 0),
    totalWeight: Number(f.TotalWeight) > 0
      ? Number(f.TotalWeight)
      : (Array.isArray(f.ProductJson)
        ? f.ProductJson.reduce((sum: number, p: any) => sum + (Number(p?.Weight) || 0) * (Number(p?.Quantity) || 1), 0)
        : 0),
    isTruckLocked: !!f.IsTruckLocked || opsStatuses.some((s: any) => s?.Status === 'Vehicle Locked'),
    linkedOrder: extractLinkedOrderId(f),
    linkedRef: extractLinkedRef(f),
    raw: doc.data(),
  }
}
